import styled from 'styled-components';

import { media } from '../../../utils/mediaQueries';

export const UserMenuTrigger = styled.button`
  && {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    transition: background-color 0.15s ease;
    width: auto;
    height: auto;
    border-radius: 999px;
    color: inherit;

    &:hover {
      opacity: 1;
      transform: none;
    }

    &:focus-visible {
      outline: 2px solid rgba(107, 114, 128, 0.5);
      outline-offset: 2px;
      border-radius: 50%;
    }

    &:active {
      transform: scale(0.95);
    }
  }
`;

/**
 * Wrapper wokół <Avatar> z react-avatar (trigger w TopBar). react-avatar
 * renderuje własny, inline-stylowany okrągły element ze zdjęciem lub
 * inicjałami — box dokłada tylko ramkę + hover spójny z poprzednim awatarem.
 */
export const AvatarBox = styled.div<{ $variant?: 'default' | 'light' }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  line-height: 0;
  flex-shrink: 0;
  cursor: pointer;
  transition: border-color 0.2s ease;
  border: 1px solid ${({ $variant, theme }) => {
    if ($variant === 'light') return '#000000';
    return theme.mode === 'dark' ? '#374151' : '#e5e7eb';
  }};

  &:hover {
    border-color: ${({ $variant, theme }) => {
    if ($variant === 'light') return '#000000';
    return theme.mode === 'dark' ? '#4b5563' : '#d1d5db';
  }};
  }
`;

/* Chevron up/down obok awatara — sygnalizuje rozwijane menu użytkownika. */
export const AvatarChevron = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#d1d5db' : '#4b5563')};
  transition: color 0.15s ease;

  ${UserMenuTrigger}:hover & {
    color: ${({ theme }) => (theme.mode === 'dark' ? '#f9fafb' : '#111827')};
  }
`;

/* Wrapper awatara w headerze dropdownu — ramka theme-aware (jak dawny LargeAvatar). */
export const HeaderAvatarBox = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  line-height: 0;
  flex-shrink: 0;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#4b5563' : '#d1d5db')};
`;

export const DropdownContainer = styled.div`
  background: ${({ theme }) => (theme.mode === 'dark' ? '#1a1f26' : '#ffffff')};
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.04);
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#e5e7eb')};
  padding: 6px;
  min-width: 220px;
  max-width: 280px;
  animation: slideIn 0.15s ease-out;
  overflow: hidden;

  ${media.mobile} {
    min-width: 240px;
    max-width: calc(100vw - 32px);
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

/* Kompaktowy header dropdownu — awatar po lewej + imię/email po prawej.
   Mała wysokość, jedna pozioma linia zamiast wielkiego centrowanego info. */
export const UserInfoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px 10px;
  margin-bottom: 4px;
  border-bottom: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#eef0f3')};
`;

/* Wrap dla imienia + emaila po prawej stronie awatara. */
export const CompactUserInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  gap: 1px;
`;

export const UserName = styled.div`
  font-size: 13px;
  font-weight: 300;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#f3f4f6' : '#1f2937')};
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const UserEmail = styled.div`
  font-size: 11px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ManageAccountButton = styled.button`
  padding: 10px 24px;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  background: ${({ theme }) => theme.colors.card.background};
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  ${media.mobile} {
    padding: 8px 20px;
    font-size: 13px;
  }

  &:hover {
    background: ${({ theme }) => (theme.mode === 'dark' ? '#242a33' : '#f9fafb')};
    border-color: ${({ theme }) => theme.colors.border.secondary};
  }

  &:active {
    transform: scale(0.98);
  }

  &:focus-visible {
    outline: 2px solid #1a4d4d;
    outline-offset: 2px;
  }
`;

export const MenuSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

export const MenuItem = styled.button<{ $danger?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border: none;
  background: transparent;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ $danger, theme }) => {
    if ($danger) return '#dc2626';
    return theme.mode === 'dark' ? '#d1d5db' : '#374151';
  }};
  cursor: pointer;
  transition: background 0.12s ease;
  text-align: left;

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    color: ${({ $danger, theme }) => {
    if ($danger) return '#dc2626';
    return theme.mode === 'dark' ? '#9ca3af' : '#6b7280';
  }};
  }

  &:hover {
    background: ${({ $danger, theme }) => {
    if ($danger) return 'rgba(239, 68, 68, 0.08)';
    return theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#f3f4f6';
  }};
  }

  &:focus-visible {
    outline: 2px solid ${({ $danger }) => ($danger ? '#ef4444' : '#1a4d4d')};
    outline-offset: -2px;
  }
`;

export const MenuItemLabel = styled.div`
  flex: 1;
  line-height: 1.4;
`;

export const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#eef0f3')};
  margin: 4px 2px;
`;

export const FooterSection = styled.div`
  padding: 8px 16px;
  border-top: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#e5e7eb')};

  ${media.mobile} {
    padding: 8px 12px;
  }
`;

export const FooterLinks = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#6b7280' : '#9ca3af')};
  flex-wrap: wrap;

  ${media.mobile} {
    gap: 8px;
    font-size: 11px;
  }

  a {
    color: ${({ theme }) => (theme.mode === 'dark' ? '#6b7280' : '#9ca3af')};
    text-decoration: none;
    transition: color 0.2s ease;
    white-space: nowrap;

    &:hover {
      color: ${({ theme }) => (theme.mode === 'dark' ? '#d1d5db' : '#374151')};
      text-decoration: underline;
    }

    &:focus-visible {
      outline: 2px solid #1a4d4d;
      outline-offset: 2px;
      border-radius: 6px;
    }
  }

  span {
    color: ${({ theme }) => (theme.mode === 'dark' ? '#4b5563' : '#d1d5db')};
    user-select: none;
  }
`;

/**
 * Zielona kropka statusu personelu — nakładka w prawym dolnym rogu avatara.
 * Widoczna tylko dla kont z claimem `isStaff === true` (render warunkowy w
 * UserMenu). Biały pierścień oddziela ją od zdjęcia/inicjałów pod spodem.
 */
// Kropka statusu na awatarze: zielona = personel, NIEBIESKA = właściciel
// organizacji. Właściciel też jest personelem, więc kolor rozróżnia rolę,
// zamiast dokładać drugi znacznik obok.
export const StaffDot = styled.span<{ $owner?: boolean }>`
  position: absolute;
  right: -1px;
  bottom: -1px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $owner }) => ($owner ? '#3b82f6' : '#10b981')};
  border: 2px solid ${({ theme }) => (theme.mode === 'dark' ? '#1a1f26' : '#ffffff')};
  box-shadow: 0 0 0 1px
    ${({ $owner }) => ($owner ? 'rgba(59, 130, 246, 0.35)' : 'rgba(16, 185, 129, 0.35)')};
  pointer-events: none;
`;
